import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getSocietyAdminData,
  getAdminSocietyDetail,
  getFlatLedgerStatement,
  getGuestCheckIns,
  getVehiclesByFlat,
  getSocietyBillsByFlatId,
  resolveDocumentImageUrl,
  revokeBlobUrl,
  AdminSocietyFlat,
  CategoryLedgerBalance,
  LedgerStatementLine,
  GuestCheckIn,
  Vehicle,
} from '../services/api';
import {
  formatBillingDateOnly,
  formatBillingDateTime,
  localMonthRangeToUtcDateStrings,
} from '../utils/billingDateUtils';
import {
  HiSearch,
  HiDocumentText,
  HiUsers,
  HiTruck,
  HiRefresh,
  HiExclamation,
  HiChevronRight,
  HiDownload,
} from '../components/Icons';
import './SocietyAdminFlatHistory.css';

type TabKey = 'ledger' | 'visitors' | 'vehicles';

const formatCurrency = (amount: number | null | undefined): string => {
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return '—';
  return (
    '\u20B9' +
    Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );
};

const formatSignedCurrency = (amount: number | null | undefined): string => {
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '\u2212' : '';
  return sign + '\u20B9' + Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (iso: string | null | undefined): string => formatBillingDateOnly(iso);

const formatDateTime = (iso: string | null | undefined): string => formatBillingDateTime(iso);

const ledgerLineDateLabel = (e: LedgerStatementLine): string => {
  const withTime =
    e.entry_type === 'payment' ||
    e.entry_type === 'payment_reversal' ||
    e.entry_type === 'payment_pending';
  const src =
    e.entry_type === 'bill_generated' && e.bill_billing_date
      ? e.bill_billing_date
      : e.transaction_date;
  return withTime ? formatDateTime(src) : formatDate(src);
};

const humanizeLedgerEntryType = (entryType: string): string =>
  entryType
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const ledgerLineDescription = (e: LedgerStatementLine): string => {
  const title = [e.bill_title, e.bill_description].filter(Boolean).join(' — ');
  if (title) return title;
  if (e.remarks) return e.remarks;
  return '—';
};

/** Statement uses positive transaction_amount + entry_side for debit/credit. */
const signedTransactionAmount = (e: LedgerStatementLine): number => {
  const mag = Math.abs(Number(e.transaction_amount) || 0);
  if (e.entry_side === 'credit') return -mag;
  if (e.entry_side === 'debit') return mag;
  return 0;
};

const flatLabel = (flat: AdminSocietyFlat): string =>
  flat.block_name ? `${flat.block_name} - ${flat.flat_number}` : flat.flat_number;

/** Month picker value → UTC dateFrom/dateTo for API (inclusive). */
const monthToDateRange = (yyyyMm: string): { dateFrom: string; dateTo: string } =>
  localMonthRangeToUtcDateStrings(yyyyMm);

const currentMonthYyyyMm = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const csvEscapeCell = (v: unknown): string => {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (filename: string, rows: string[][]) => {
  const bom = '\uFEFF';
  const body = rows.map((r) => r.map(csvEscapeCell).join(',')).join('\r\n');
  const blob = new Blob([bom + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const SocietyAdminFlatHistory: React.FC = () => {
  const [searchParams] = useSearchParams();
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [flats, setFlats] = useState<AdminSocietyFlat[]>([]);
  const [flatsLoading, setFlatsLoading] = useState(true);
  const [flatsError, setFlatsError] = useState('');

  const [selectedFlatId, setSelectedFlatId] = useState<number | null>(null);
  const [flatSearch, setFlatSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<TabKey>('ledger');

  const [balances, setBalances] = useState<CategoryLedgerBalance[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<LedgerStatementLine[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState('');
  /** `null` = all months (no date filter). Default: current calendar month. */
  const [ledgerMonth, setLedgerMonth] = useState<string | null>(() => currentMonthYyyyMm());

  const [visitors, setVisitors] = useState<GuestCheckIn[]>([]);
  const [visitorsLoading, setVisitorsLoading] = useState(false);
  const [visitorsError, setVisitorsError] = useState('');
  const [visitorPhotoUrls, setVisitorPhotoUrls] = useState<Record<number, string>>({});
  const [photoViewerId, setPhotoViewerId] = useState<number | null>(null);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesError, setVehiclesError] = useState('');

  const [billsReportLoading, setBillsReportLoading] = useState(false);
  const [downloadNotice, setDownloadNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(
    null
  );

  useEffect(() => {
    if (!societyId) {
      setFlatsLoading(false);
      setFlatsError('No society selected. Please select a society from the menu.');
      return;
    }
    const load = async () => {
      setFlatsLoading(true);
      setFlatsError('');
      try {
        const res = await getAdminSocietyDetail(societyId);
        setFlats(res.data.flats ?? []);
      } catch (err: any) {
        setFlatsError(err.message || 'Failed to load flats');
      } finally {
        setFlatsLoading(false);
      }
    };
    load();
  }, [societyId]);

  useEffect(() => {
    const flatIdParam = searchParams.get('flatId');
    if (!flatIdParam) return;
    const id = parseInt(flatIdParam, 10);
    if (Number.isFinite(id) && flats.some((f) => f.id === id)) {
      setSelectedFlatId(id);
      setActiveTab('ledger');
    }
  }, [searchParams, flats]);

  const groupedFlats = useMemo(() => {
    const groups: Record<string, AdminSocietyFlat[]> = {};
    flats.forEach((f) => {
      const block = f.block_name || 'Others';
      if (!groups[block]) groups[block] = [];
      groups[block].push(f);
    });
    const sorted: Record<string, AdminSocietyFlat[]> = {};
    Object.keys(groups)
      .sort()
      .forEach((block) => {
        sorted[block] = groups[block].sort((a, b) => {
          if (a.floor !== b.floor) return (a.floor || 0) - (b.floor || 0);
          return a.flat_number.localeCompare(b.flat_number);
        });
      });
    return sorted;
  }, [flats]);

  const filteredGroupedFlats = useMemo(() => {
    if (!flatSearch.trim()) return groupedFlats;
    const q = flatSearch.toLowerCase();
    const result: Record<string, AdminSocietyFlat[]> = {};
    Object.entries(groupedFlats).forEach(([block, blockFlats]) => {
      const matching = blockFlats.filter(
        (f) =>
          f.flat_number.toLowerCase().includes(q) ||
          (f.block_name && f.block_name.toLowerCase().includes(q))
      );
      if (matching.length) result[block] = matching;
    });
    return result;
  }, [groupedFlats, flatSearch]);

  const selectedFlat = useMemo(
    () => flats.find((f) => f.id === selectedFlatId) ?? null,
    [flats, selectedFlatId]
  );

  const fetchLedger = useCallback(async () => {
    if (!selectedFlatId) return;
    setLedgerLoading(true);
    setLedgerError('');
    try {
      const range = ledgerMonth ? monthToDateRange(ledgerMonth) : undefined;
      const stmtRes = await getFlatLedgerStatement(selectedFlatId, societyId, range);
      const lines = stmtRes.data?.lines ?? [];
      setBalances(stmtRes.data?.balances ?? []);
      setLedgerEntries(
        [...lines].sort((a, b) => {
          const tb = new Date(b.transaction_date).getTime();
          const ta = new Date(a.transaction_date).getTime();
          if (tb !== ta) return tb - ta;
          return b.id - a.id;
        })
      );
    } catch (err: any) {
      setLedgerError(err.message || 'Failed to load ledger');
    } finally {
      setLedgerLoading(false);
    }
  }, [selectedFlatId, societyId, ledgerMonth]);

  const fetchVisitors = useCallback(async () => {
    if (!selectedFlatId) return;
    setVisitorsLoading(true);
    setVisitorsError('');
    try {
      const res = await getGuestCheckIns(societyId, selectedFlatId);
      setVisitors(res.data ?? []);
    } catch (err: any) {
      setVisitorsError(err.message || 'Failed to load visitors');
    } finally {
      setVisitorsLoading(false);
    }
  }, [selectedFlatId, societyId]);

  const fetchVehicles = useCallback(async () => {
    if (!selectedFlatId) return;
    setVehiclesLoading(true);
    setVehiclesError('');
    try {
      const res = await getVehiclesByFlat(selectedFlatId);
      setVehicles(res.data ?? []);
    } catch (err: any) {
      setVehiclesError(err.message || 'Failed to load vehicles');
    } finally {
      setVehiclesLoading(false);
    }
  }, [selectedFlatId]);

  useEffect(() => {
    if (!selectedFlatId) return;
    if (activeTab === 'visitors') fetchVisitors();
    else if (activeTab === 'vehicles') fetchVehicles();
  }, [selectedFlatId, activeTab, fetchVisitors, fetchVehicles]);

  useEffect(() => {
    const urls: Record<number, string> = {};
    let cancelled = false;
    const resolve = async () => {
      for (const v of visitors) {
        const photoPath = (v as any).visitor_photo_path;
        if (photoPath) {
          try {
            urls[v.id] = await resolveDocumentImageUrl(photoPath);
          } catch { /* skip */ }
        }
      }
      if (!cancelled) setVisitorPhotoUrls(urls);
    };
    if (visitors.length) resolve();
    return () => {
      cancelled = true;
      Object.values(urls).forEach(u => revokeBlobUrl(u));
    };
  }, [visitors]);

  useEffect(() => {
    if (!selectedFlatId || activeTab !== 'ledger') return;
    fetchLedger();
  }, [selectedFlatId, activeTab, ledgerMonth, fetchLedger]);

  const handleSelectFlat = (flatId: number) => {
    setSelectedFlatId(flatId);
    setDropdownOpen(false);
    setFlatSearch('');
    setActiveTab('ledger');
  };

  const handleRefresh = () => {
    if (activeTab === 'ledger') fetchLedger();
    else if (activeTab === 'visitors') fetchVisitors();
    else if (activeTab === 'vehicles') fetchVehicles();
  };

  const flatSlug = (flat: AdminSocietyFlat) =>
    flatLabel(flat)
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9\-_]/g, '_');

  const downloadFlatBillsCsv = async () => {
    if (!selectedFlat || !selectedFlatId) return;
    setBillsReportLoading(true);
    setDownloadNotice(null);
    try {
      const res = await getSocietyBillsByFlatId(selectedFlatId);
      let rows = res.data ?? [];
      if (ledgerMonth) {
        const { dateFrom, dateTo } = monthToDateRange(ledgerMonth);
        rows = rows.filter((b) => b.billing_date >= dateFrom && b.billing_date <= dateTo);
      }
      const csvRows: string[][] = [
        [
          'id',
          'billing_date',
          'due_date',
          'title',
          'bill_type',
          'amount',
          'amount_paid',
          'interest_amount',
          'status',
          'remarks',
          'created_on',
        ],
        ...rows.map((b) => [
          String(b.id),
          b.billing_date,
          b.due_date ?? '',
          b.title ?? '',
          b.bill_type,
          String(b.amount),
          String(b.amount_paid),
          String(b.interest_amount),
          b.status,
          b.remarks ?? '',
          b.created_on,
        ]),
      ];
      const suffix = ledgerMonth ?? 'all-months';
      downloadCsv(`${flatSlug(selectedFlat)}-society-bills-${suffix}.csv`, csvRows);
      setDownloadNotice({ kind: 'ok', text: 'Bills CSV downloaded.' });
    } catch (err: any) {
      setDownloadNotice({ kind: 'err', text: err.message || 'Failed to download bills CSV.' });
    } finally {
      setBillsReportLoading(false);
    }
  };

  const downloadLedgerLinesCsv = () => {
    if (!selectedFlat || !selectedFlatId) return;
    setDownloadNotice(null);
    const suffix = ledgerMonth ?? 'all-months';
    const csvRows: string[][] = [
      [
        'id',
        'date',
        'due_date',
        'bill_type',
        'entry_type',
        'entry_side',
        'description',
        'transaction_amount_signed',
        'principal_delta',
        'interest_delta',
        'advance_delta',
        'total_due_after',
        'remarks',
        'reference_type',
        'reference_id',
      ],
      ...ledgerEntries.map((e) => {
        const signed = signedTransactionAmount(e);
        return [
          String(e.id),
          ledgerLineDateLabel(e),
          e.due_date ?? '',
          e.bill_type,
          e.entry_type,
          e.entry_side,
          ledgerLineDescription(e),
          String(signed),
          String(e.principal_delta),
          String(e.interest_delta),
          String(e.advance_delta),
          String(e.total_due_after),
          e.remarks ?? '',
          e.reference_type ?? '',
          e.reference_id != null ? String(e.reference_id) : '',
        ];
      }),
    ];
    downloadCsv(`${flatSlug(selectedFlat)}-ledger-lines-${suffix}.csv`, csvRows);
    setDownloadNotice({ kind: 'ok', text: 'Ledger CSV downloaded.' });
  };

  const downloadBalancesCsv = () => {
    if (!selectedFlat) return;
    setDownloadNotice(null);
    const csvRows: string[][] = [
      [
        'bill_type',
        'principal_outstanding',
        'interest_outstanding',
        'advance_balance',
        'interest_exempt_principal',
        'total_due',
        'oldest_unpaid_due_date',
      ],
      ...balances.map((b) => [
        b.bill_type,
        String(b.principal_outstanding),
        String(b.interest_outstanding),
        String(b.advance_balance),
        String(b.interest_exempt_principal),
        String(b.total_due),
        b.oldest_unpaid_due_date ?? '',
      ]),
    ];
    downloadCsv(`${flatSlug(selectedFlat)}-ledger-balances.csv`, csvRows);
    setDownloadNotice({ kind: 'ok', text: 'Balances CSV downloaded.' });
  };

  const downloadVisitorsCsv = () => {
    if (!selectedFlat) return;
    setDownloadNotice(null);
    const csvRows: string[][] = [
      [
        'guest_name',
        'guest_phone',
        'purpose',
        'vehicle_number',
        'check_in_time',
        'check_out_time',
        'guard_name',
        'status',
        'approved_by',
      ],
      ...visitors.map((v) => [
        v.guest_name,
        v.guest_phone ?? '',
        v.purpose ?? '',
        v.vehicle_number ?? '',
        v.check_in_time,
        v.check_out_time ?? '',
        v.guard_name ?? '',
        v.status ?? '',
        v.approved_by ?? '',
      ]),
    ];
    downloadCsv(`${flatSlug(selectedFlat)}-visitors.csv`, csvRows);
    setDownloadNotice({ kind: 'ok', text: 'Visitors CSV downloaded.' });
  };

  const downloadVehiclesCsv = () => {
    if (!selectedFlat) return;
    setDownloadNotice(null);
    const csvRows: string[][] = [
      [
        'vehicle_number',
        'vehicle_type',
        'make',
        'model',
        'color',
        'owner_name',
        'created_at',
      ],
      ...vehicles.map((v) => [
        v.vehicle_number,
        v.vehicle_type ?? '',
        v.make ?? '',
        v.model ?? '',
        v.color ?? '',
        v.owner_name ?? '',
        v.created_at ?? '',
      ]),
    ];
    downloadCsv(`${flatSlug(selectedFlat)}-vehicles.csv`, csvRows);
    setDownloadNotice({ kind: 'ok', text: 'Vehicles CSV downloaded.' });
  };

  const balanceColors: Record<number, string> = { 0: '#10b981', 1: '#3b82f6', 2: '#f59e0b', 3: '#8b5cf6', 4: '#ec4899', 5: '#ef4444' };

  const renderBalanceCards = () => (
    <div className="safh-balance-grid">
      {balances.map((b, i) => (
        <div
          key={b.bill_type}
          className="safh-balance-card"
          style={{ borderLeftColor: balanceColors[i % Object.keys(balanceColors).length] }}
        >
          <div className="safh-balance-type">{b.bill_type}</div>
          <div className="safh-balance-total">{formatCurrency(b.total_due)}</div>
          <div className="safh-balance-breakdown">
            <span>Principal: {formatCurrency(b.principal_outstanding)}</span>
            <span>Interest: {formatCurrency(b.interest_outstanding)}</span>
            {b.advance_balance > 0 && <span className="safh-advance">Advance: {formatCurrency(b.advance_balance)}</span>}
          </div>
          {b.oldest_unpaid_due_date && (
            <div className="safh-balance-oldest">
              Oldest due: {formatDate(b.oldest_unpaid_due_date)}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderLedgerTable = () => {
    if (!ledgerEntries.length) {
      return (
        <div className="safh-empty">
          <HiDocumentText size={32} />
          <p>No ledger entries found for this flat.</p>
        </div>
      );
    }
    return (
      <div className="safh-table-wrapper">
        <table className="safh-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Category</th>
              <th>Type</th>
              <th>Description</th>
              <th className="safh-col-right">Amount</th>
              <th className="safh-col-right">Principal Δ</th>
              <th className="safh-col-right">Interest Δ</th>
              <th className="safh-col-right">Total due</th>
            </tr>
          </thead>
          <tbody>
            {ledgerEntries.map((e) => {
              const signed = signedTransactionAmount(e);
              const isDebitRow = e.entry_side === 'debit';
              return (
                <tr key={e.id} className={isDebitRow ? 'safh-row-debit' : ''}>
                  <td className="safh-cell-nowrap">{ledgerLineDateLabel(e)}</td>
                  <td>{e.bill_type}</td>
                  <td>
                    <span
                      className={`safh-badge ${
                        e.entry_side === 'credit'
                          ? 'safh-badge-green'
                          : e.entry_side === 'debit'
                            ? 'safh-badge-red'
                            : 'safh-badge-gray'
                      }`}
                    >
                      {humanizeLedgerEntryType(e.entry_type)}
                    </span>
                  </td>
                  <td>{ledgerLineDescription(e)}</td>
                  <td
                    className={`safh-col-right ${signed < 0 ? 'safh-text-green' : signed > 0 ? 'safh-text-red' : ''}`}
                  >
                    {formatSignedCurrency(signed)}
                  </td>
                  <td className="safh-col-right">
                    {e.principal_delta !== 0 ? formatSignedCurrency(e.principal_delta) : '—'}
                  </td>
                  <td className="safh-col-right">
                    {e.interest_delta !== 0 ? formatSignedCurrency(e.interest_delta) : '—'}
                  </td>
                  <td className="safh-col-right safh-cell-bold">{formatCurrency(e.total_due_after)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderVisitorsTable = () => {
    if (!visitors.length) {
      return (
        <div className="safh-empty">
          <HiUsers size={32} />
          <p>No visitor records found for this flat.</p>
        </div>
      );
    }
    return (
      <div className="safh-table-wrapper">
        <table className="safh-table">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Guest Name</th>
              <th>Phone</th>
              <th>Purpose</th>
              <th>Vehicle</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Guard</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {visitors.map((v) => (
              <tr key={v.id}>
                <td>
                  {visitorPhotoUrls[v.id] ? (
                    <img
                      src={visitorPhotoUrls[v.id]}
                      alt="Visitor"
                      className="safh-visitor-photo"
                      onClick={() => setPhotoViewerId(v.id)}
                    />
                  ) : (
                    <span className="safh-no-photo">—</span>
                  )}
                </td>
                <td className="safh-cell-bold">{v.guest_name}</td>
                <td>{v.guest_phone || '-'}</td>
                <td>{v.purpose || '-'}</td>
                <td className="safh-cell-mono">{v.vehicle_number || '-'}</td>
                <td className="safh-cell-nowrap">{formatDateTime(v.check_in_time)}</td>
                <td className="safh-cell-nowrap">{v.check_out_time ? formatDateTime(v.check_out_time) : '-'}</td>
                <td>{v.guard_name || '-'}</td>
                <td>
                  <span className={`safh-badge ${v.status === 'checked_out' ? 'safh-badge-gray' : 'safh-badge-green'}`}>
                    {v.status || 'active'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {photoViewerId !== null && visitorPhotoUrls[photoViewerId] && (
          <div className="safh-photo-overlay" onClick={() => setPhotoViewerId(null)}>
            <img src={visitorPhotoUrls[photoViewerId]} alt="Visitor" className="safh-photo-full" />
          </div>
        )}
      </div>
    );
  };

  const renderVehiclesTable = () => {
    if (!vehicles.length) {
      return (
        <div className="safh-empty">
          <HiTruck size={32} />
          <p>No vehicles registered to this flat.</p>
        </div>
      );
    }
    return (
      <div className="safh-table-wrapper">
        <table className="safh-table">
          <thead>
            <tr>
              <th>Vehicle Number</th>
              <th>Type</th>
              <th>Make / Model</th>
              <th>Color</th>
              <th>Owner</th>
              <th>Registered On</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v.id}>
                <td className="safh-cell-mono safh-cell-bold">{v.vehicle_number}</td>
                <td>{v.vehicle_type || '-'}</td>
                <td>{[v.make, v.model].filter(Boolean).join(' ') || '-'}</td>
                <td>{v.color || '-'}</td>
                <td>{v.owner_name || '-'}</td>
                <td className="safh-cell-nowrap">{v.created_at ? formatDate(v.created_at) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTabContent = () => {
    const tabConfig: Record<TabKey, { loading: boolean; error: string; render: () => React.ReactNode }> = {
      ledger: {
        loading: ledgerLoading,
        error: ledgerError,
        render: () => (
          <>
            <div className="safh-ledger-toolbar">
              <label className="safh-ledger-month-label" htmlFor="safh-ledger-month">
                Month
              </label>
              <input
                id="safh-ledger-month"
                type="month"
                className="safh-ledger-month-input"
                value={ledgerMonth ?? ''}
                onChange={(e) => setLedgerMonth(e.target.value ? e.target.value : null)}
              />
              <button
                type="button"
                className="safh-btn safh-btn-outline safh-btn-sm"
                onClick={() => setLedgerMonth(null)}
              >
                All months
              </button>
              <button
                type="button"
                className="safh-btn safh-btn-outline safh-btn-sm"
                onClick={() => setLedgerMonth(currentMonthYyyyMm())}
              >
                This month
              </button>
              <span className="safh-ledger-toolbar-hint">
                {ledgerMonth
                  ? `Ledger rows are filtered by transaction date in this month. Category cards remain current totals.`
                  : 'Ledger shows recent activity across all months (server limit applies).'}
              </span>
              <div className="safh-ledger-reports">
                <span className="safh-ledger-reports-label">Download</span>
                <button
                  type="button"
                  className="safh-btn safh-btn-outline safh-btn-sm"
                  disabled={billsReportLoading || ledgerLoading}
                  onClick={() => void downloadFlatBillsCsv()}
                >
                  <HiDownload size={14} /> Bills (CSV)
                </button>
                <button
                  type="button"
                  className="safh-btn safh-btn-outline safh-btn-sm"
                  disabled={ledgerLoading}
                  onClick={downloadLedgerLinesCsv}
                >
                  <HiDownload size={14} /> Ledger (CSV)
                </button>
                <button
                  type="button"
                  className="safh-btn safh-btn-outline safh-btn-sm"
                  disabled={ledgerLoading}
                  onClick={downloadBalancesCsv}
                >
                  <HiDownload size={14} /> Balances (CSV)
                </button>
              </div>
            </div>
            {balances.length > 0 && renderBalanceCards()}
            {renderLedgerTable()}
          </>
        ),
      },
      visitors: { loading: visitorsLoading, error: visitorsError, render: renderVisitorsTable },
      vehicles: { loading: vehiclesLoading, error: vehiclesError, render: renderVehiclesTable },
    };

    const current = tabConfig[activeTab];

    if (current.loading) {
      return (
        <div className="safh-loading">
          <HiRefresh size={24} className="spin" />
          <span>Loading...</span>
        </div>
      );
    }

    if (current.error) {
      return (
        <div className="safh-error">
          <HiExclamation size={24} />
          <span>{current.error}</span>
          <button className="safh-btn safh-btn-outline" onClick={handleRefresh}>
            <HiRefresh size={14} /> Retry
          </button>
        </div>
      );
    }

    return current.render();
  };

  if (flatsLoading) {
    return (
      <div className="safh-page">
        <div className="safh-loading">
          <HiRefresh size={24} className="spin" />
          <span>Loading flats...</span>
        </div>
      </div>
    );
  }

  if (flatsError) {
    return (
      <div className="safh-page">
        <div className="safh-error">
          <HiExclamation size={24} />
          <span>{flatsError}</span>
        </div>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
    { key: 'ledger', label: 'Bills & Ledger', icon: <HiDocumentText size={16} /> },
    { key: 'visitors', label: 'Visitors', icon: <HiUsers size={16} /> },
    { key: 'vehicles', label: 'Vehicles', icon: <HiTruck size={16} /> },
  ];

  return (
    <div className="safh-page">
      <div className="safh-top-bar">
        <h2>
          <HiDocumentText size={22} />
          Flat History
        </h2>
      </div>

      <div className="safh-flat-selector">
        <div className="safh-dropdown-container">
          <div
            className={`safh-dropdown-trigger ${dropdownOpen ? 'safh-dropdown-trigger-open' : ''}`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <HiSearch size={16} className="safh-dropdown-icon" />
            <span className={selectedFlat ? 'safh-dropdown-value' : 'safh-dropdown-placeholder'}>
              {selectedFlat ? flatLabel(selectedFlat) : 'Select a flat...'}
            </span>
            <HiChevronRight size={16} className={`safh-dropdown-chevron ${dropdownOpen ? 'safh-dropdown-chevron-open' : ''}`} />
          </div>

          {dropdownOpen && (
            <>
              <div className="safh-dropdown-backdrop" onClick={() => setDropdownOpen(false)} />
              <div className="safh-dropdown-menu">
                <div className="safh-dropdown-search">
                  <HiSearch size={14} />
                  <input
                    type="text"
                    placeholder="Search flat..."
                    value={flatSearch}
                    onChange={(e) => setFlatSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="safh-dropdown-options">
                  {Object.entries(filteredGroupedFlats).length === 0 && (
                    <div className="safh-dropdown-empty">No flats found</div>
                  )}
                  {Object.entries(filteredGroupedFlats).map(([block, blockFlats]) => (
                    <div key={block} className="safh-dropdown-group">
                      <div className="safh-dropdown-group-label">{block}</div>
                      {blockFlats.map((f) => (
                        <div
                          key={f.id}
                          className={`safh-dropdown-option ${f.id === selectedFlatId ? 'safh-dropdown-option-active' : ''}`}
                          onClick={() => handleSelectFlat(f.id)}
                        >
                          <span>{f.flat_number}</span>
                          {f.users.length > 0 && (
                            <span className="safh-dropdown-user-count">{f.users.length} member{f.users.length !== 1 ? 's' : ''}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {!selectedFlatId && (
        <div className="safh-prompt">
          <HiSearch size={40} />
          <h3>Select a flat to view its history</h3>
          <p>Choose a flat from the dropdown above to view bills, visitors, and vehicle records.</p>
        </div>
      )}

      {selectedFlatId && (
        <div className="safh-content">
          <div className="safh-tabs-bar">
            <div className="safh-tabs">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  className={`safh-tab ${activeTab === t.key ? 'safh-tab-active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
            </div>
            <div className="safh-tabs-actions">
              {activeTab === 'visitors' && (
                <button
                  type="button"
                  className="safh-btn safh-btn-outline safh-btn-sm"
                  disabled={visitorsLoading}
                  onClick={downloadVisitorsCsv}
                >
                  <HiDownload size={14} /> Visitors (CSV)
                </button>
              )}
              {activeTab === 'vehicles' && (
                <button
                  type="button"
                  className="safh-btn safh-btn-outline safh-btn-sm"
                  disabled={vehiclesLoading}
                  onClick={downloadVehiclesCsv}
                >
                  <HiDownload size={14} /> Vehicles (CSV)
                </button>
              )}
              <button className="safh-btn safh-btn-outline safh-btn-sm" onClick={handleRefresh}>
                <HiRefresh size={14} /> Refresh
              </button>
            </div>
          </div>

          {downloadNotice && (
            <div
              className={`safh-download-notice ${downloadNotice.kind === 'err' ? 'safh-download-notice-error' : ''}`}
            >
              {downloadNotice.kind === 'err' ? <HiExclamation size={16} /> : null}
              {downloadNotice.text}
            </div>
          )}

          <div className="safh-tab-content">{renderTabContent()}</div>
        </div>
      )}
    </div>
  );
};

export default SocietyAdminFlatHistory;
